import type { FormikProps } from 'formik'
import type { FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import { Form, useLanguage } from '@monthio/react-components'
import { Formik } from 'formik'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormikValues } from './types'

import { useCustomFormModel } from './model/useCustomFormModel'
import { QuestionComponent } from './Question'
import { useCustomQuestionnaireModule } from './useCustomQuestionnaireModule'

export const CustomQuestionnaire: FunctionComponent = () => {
	const { t } = useTranslation()
	const formikRef = useRef<FormikProps<FormikValues>>(null)
	const { data, finish } = useCustomQuestionnaireModule()

	const language = useLanguage()
	const {
		questions: { data: newQuestions },
		title,
		submit,
		validate,
	} = useCustomFormModel(language, data, finish)

	useEffect(() => {
		if (newQuestions?.length) {
			formikRef.current?.validateForm()
		}
	}, [newQuestions])

	return (
		<Formik
			innerRef={formikRef}
			enableReinitialize
			initialValues={{}}
			onSubmit={async (values) => submit?.(values)}
			validate={(values) => validate?.(values)}
			validateOnChange
			validateOnMount
		>
			{(formProps) => {
				const isDisabled = Object.keys(formProps.errors).length > 0
				return (
					<Form
						style={{
							height: '100%',
						}}
					>
						<FlowPage>
							<FlowPage.Content $spacing="xl">
								<FlowPage.Header
									title={title ?? t('customQuestionnaire-default-title')}
								/>
								{newQuestions?.map((x) => {
									return (
										<QuestionComponent
											key={x.id}
											formProps={formProps}
											question={x}
										/>
									)
								})}
							</FlowPage.Content>
							<FlowPage.Footer
								actions={
									<FlowPage.Cta
										disabled={formProps.isSubmitting || isDisabled}
										onClick={async () => {
											await formProps.submitForm()
										}}
									>
										{t('customQuestionnaire-continue-text')}
									</FlowPage.Cta>
								}
							/>
						</FlowPage>
					</Form>
				)
			}}
		</Formik>
	)
}
